import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';

import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import Loader from 'components/partials/loader/loader';
import Tabs from 'components/partials/tabs/tabs';
import toast from 'components/partials/toast/toast';

import AddRegimeModal from './components/add-regime-modal';
import AwardCalculationTabContent from './components/award-calculation-tab-content';
import RegimesSidebar from './components/regimes-sidebar';
import RegimesSidebarToggle from './components/regimes-sidebar-toggle';
import DefinitionTabContent from './components/definition-tab-content';
import PopulationsTabContent from './components/populations-tab-content';
import UnsavedChangesModal from './components/unsaved-changes-modal';

import useRegimeDraft from './hooks/use-regime-draft';

import type {
  RegimeItem as RegimeItemFromHook,
  RegimeGroup as RegimeGroupFromHook,
} from './hooks/use-regimes-sidebar-actions';

import {
  useRegimeGroups,
  useRegimes,
  useCustomLogic,
  useAcademicRanks,
  useValidateRegimeGroup,
  regimeGroupValidationKeys,
} from '../../hooks/use-aid-regimes';
import { useAidModelDataset } from '../../hooks/use-aid-model-details';

import { RegimeSelectionProvider } from './hooks/regime-selection-context';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { MATRIX_TAB, MATRIX_TABS } from './matrix-step.constants';
import { DATASET_STATUSES } from '../../aid-studio-details.constants';

import Icons from 'assets/icons';

const VALIDATION_START_TOAST_ID = 'regime-validation-progress';

const getTabStateLabel = (
  tabValue: string,
  activeTab: string,
  visitedTabs: Set<string>
): string => {
  if (tabValue === activeTab) {
    return 'current step';
  }

  if (visitedTabs.has(tabValue)) {
    return 'completed';
  }

  return 'not started';
};

const getTabIcon = (
  tabValue: string,
  activeTab: string,
  visitedTabs: Set<string>
): React.JSX.Element => {
  if (tabValue === activeTab) {
    return <Icons.EditPencil customClass="w-4 h-4" aria-hidden="true" />;
  }

  if (visitedTabs.has(tabValue)) {
    return <Icons.CheckOld customClass="w-4 h-4" aria-hidden="true" />;
  }

  return <Icons.CircleDotted customClass="w-4 h-4" aria-hidden="true" />;
};

const renderCountBadge = (label: string, count: number): React.JSX.Element => (
  <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">
    {`${label}: ${count}`}
  </span>
);

interface MatrixStepProps {
  clientId?: string | number;
  readOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  isUserView?: boolean;
}

type RegimeItem = RegimeItemFromHook & {
  customLogicIds?: (string | number)[];
};

type RegimeGroupItem = RegimeGroupFromHook;

const MatrixStep = forwardRef<unknown, MatrixStepProps>(
  ({ clientId: clientIdProp, readOnly, onDirtyChange, isUserView }, ref) => {
    const defaultTab = isUserView
      ? MATRIX_TAB.AWARD_CALCULATION
      : MATRIX_TABS[0].value;
    const [activeTab, setActiveTab] = useState<string>(defaultTab);
    const [visitedTabs, setVisitedTabs] = useState<Set<string>>(
      () => new Set([defaultTab])
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegimeId, setSelectedRegimeId] = useState<
      string | number | null
    >(null);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(
      null
    );
    const [isSavingBeforeAction, setIsSavingBeforeAction] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isToggleInTabBar, setIsToggleInTabBar] = useState(false);

    const { clientId: clientIdParam, modelId } = useParams();
    const clientId = clientIdProp ?? clientIdParam;
    const queryClient = useQueryClient();
    const currentUser = useCurrentUser() as unknown as {
      data?: { oktaUserId?: string };
    };
    const userId = currentUser?.data?.oktaUserId;

    const regimeGroupsQuery = useRegimeGroups({
      userId: userId ?? '',
      modelId: modelId ?? '',
      enabled: !!userId && !!modelId,
    });
    const regimeGroups = (regimeGroupsQuery.data ?? []) as RegimeGroupItem[];

    const regimesQuery = useRegimes({
      userId: userId ?? '',
      modelId: modelId ?? '',
      enabled: !!userId && !!modelId,
    });
    const regimesList = (regimesQuery.data ?? []) as RegimeItem[];

    const academicRanksQuery = useAcademicRanks({
      userId: userId ?? '',
      aidModelId: modelId ?? '',
      enabled: !!userId && !!modelId,
    });
    const academicRanks = (academicRanksQuery.data ?? []) as unknown[];

    const customLogicQuery = useCustomLogic({
      userId: userId ?? '',
      clientId: clientId ?? '',
      enabled: !!userId && !!clientId,
    });

    const customLogicCategories = (customLogicQuery.data ?? []) as Array<{
      category: string;
      [key: string]: unknown;
    }>;

    const datasetQuery = useAidModelDataset({
      userId: userId ?? '',
      modelId: modelId ?? '',
      enabled: !!userId && !!modelId,
    });
    const isDatasetReady =
      (datasetQuery.data as { status?: string } | undefined)?.status ===
      DATASET_STATUSES.CREATED;

    const isLoading = regimeGroupsQuery.isLoading || regimesQuery.isLoading;
    const hasRegimes = regimesList.length > 0;

    useEffect(() => {
      if (!regimesList.length) return;

      const selectedExists = regimesList.some((r) => r.id === selectedRegimeId);

      if (!selectedExists) {
        setSelectedRegimeId(regimesList[0].id);
      }
    }, [regimesList, selectedRegimeId]);

    const selectedRegime = useMemo(
      () => regimesList.find((r) => r.id === selectedRegimeId) ?? null,
      [regimesList, selectedRegimeId]
    );

    const groupId = selectedRegime?.groupId ?? '';

    const {
      data: validationData,
      isFetching: isValidationLoading,
      error: validationError,
      isSuccess: isValidationSuccess,
    } = useValidateRegimeGroup({
      userId: userId ?? '',
      modelId: modelId ?? '',
      groupId,
      enabled: isDatasetReady && !selectedRegime?.pell,
    });

    useEffect(() => {
      if (!validationError) return;

      const detail =
        (validationError as { data?: { detail?: string } })?.data?.detail ||
        'Validation failed. Please try again.';

      toast.error({ title: detail });
    }, [validationError]);

    const wasValidationFetchingRef = useRef(false);
    const lastToastGroupIdRef = useRef<typeof groupId | null>(null);
    const trackedFetchGroupIdRef = useRef<typeof groupId | null>(null);

    useEffect(() => {
      const wasFetching = wasValidationFetchingRef.current;
      const isGroupSwitch = groupId !== lastToastGroupIdRef.current;

      wasValidationFetchingRef.current = isValidationLoading;
      lastToastGroupIdRef.current = groupId;

      if (isGroupSwitch) {
        trackedFetchGroupIdRef.current = null;

        return;
      }

      if (isValidationLoading && !wasFetching) {
        trackedFetchGroupIdRef.current = groupId;
        toast.info({
          title: 'Validation process has started.',
          message: `We'll notify you once it's ready.`,
          toastId: VALIDATION_START_TOAST_ID,
        });

        return;
      }

      if (
        !isValidationLoading &&
        wasFetching &&
        isValidationSuccess &&
        trackedFetchGroupIdRef.current === groupId
      ) {
        trackedFetchGroupIdRef.current = null;
        toast.dismiss(VALIDATION_START_TOAST_ID);
        toast.success({
          title: 'Successful validation.',
          message: 'Changes were applied.',
        });
      }
    }, [isValidationLoading, isValidationSuccess, groupId]);

    const {
      awardType,
      calculatedBy,
      ranges,
      onAwardTypeChange,
      onCalculatedByChange,
      onRangesChange,

      rankIds,
      rankIdToDisplayNumber,
      getCell,
      updateCell,

      populationType,
      handlePopulationTypeChange,
      baselineSelected,
      toggleBaseline,
      criteriaBlocks,
      criteriaErrors,
      addCriteriaBlock,
      removeCriteriaBlock,
      canAddCriteriaBlock,
      handleBlockDimensionChange,
      handleBlockValuesChange,
      getDimensionOptionsForBlock,
      getValueOptions,
      pell,
      hasChanges,
      handleSave,
      handleDiscard,
    } = useRegimeDraft({
      selectedRegime: (selectedRegime ?? undefined) as
        | Record<string, unknown>
        | null
        | undefined,
      academicRanks,
      customLogicCategories,
      userId,
      modelId,
      onSaveSuccess: useCallback(() => {
        if (modelId && groupId) {
          queryClient.invalidateQueries({
            queryKey: regimeGroupValidationKeys.group(modelId, groupId),
          });
        }
      }, [modelId, groupId, queryClient]),
    });

    useImperativeHandle(
      ref,
      () => ({ save: handleSave, discard: handleDiscard, hasChanges }),
      [handleSave, handleDiscard, hasChanges]
    );

    useEffect(() => {
      onDirtyChange?.(hasChanges);
    }, [hasChanges, onDirtyChange]);

    const hasChangesRef = useRef(hasChanges);

    hasChangesRef.current = hasChanges;

    const selectedRegimeIdRef = useRef(selectedRegimeId);

    selectedRegimeIdRef.current = selectedRegimeId;

    const guardAction = useCallback(
      (action: () => void, affectedRegimeId?: string | number): void => {
        const affectsCurrentRegime =
          affectedRegimeId === undefined ||
          affectedRegimeId === selectedRegimeIdRef.current;

        if (hasChangesRef.current && affectsCurrentRegime) {
          setPendingAction(() => action);
        } else {
          action();
        }
      },
      []
    );

    const handleAddRegime = useCallback(
      (): void => guardAction(() => setIsModalOpen(true)),
      [guardAction]
    );

    // The toggle button lives in the sidebar header while expanded and in the
    // tab bar while collapsed, so it remounts on every toggle. This ref tells
    // the freshly mounted instance to take focus — but only after a real user
    // toggle, never on the initial render.
    const hasToggledSidebarRef = useRef(false);

    const handleToggleSidebar = useCallback((): void => {
      hasToggledSidebarRef.current = true;

      const next = !isSidebarCollapsed;

      // Hand the toggle over in the same tick as the collapse/expand so it's
      // never absent from the screen while the panel animates.
      setIsSidebarCollapsed(next);
      setIsToggleInTabBar(next);
    }, [isSidebarCollapsed]);

    const handleSelectRegime = useCallback(
      (regimeId: string | number): void => {
        guardAction(() => {
          setSelectedRegimeId(regimeId);
          setActiveTab(defaultTab);
          setVisitedTabs(new Set([defaultTab]));
        });
      },
      [guardAction, defaultTab]
    );

    const handleTabSelect = useCallback((tabValue: string): void => {
      setActiveTab(tabValue);
      setVisitedTabs((prev) =>
        prev.has(tabValue) ? prev : new Set(prev).add(tabValue)
      );
    }, []);

    const handleSaveAndContinue = useCallback(async () => {
      setIsSavingBeforeAction(true);
      try {
        await handleSave();
        pendingAction?.();
      } finally {
        setPendingAction(null);
        setIsSavingBeforeAction(false);
      }
    }, [handleSave, pendingAction]);

    const handleDiscardAndContinue = useCallback(() => {
      handleDiscard();
      pendingAction?.();
      setPendingAction(null);
    }, [handleDiscard, pendingAction]);

    const handleCloseUnsaved = useCallback(() => {
      setPendingAction(null);
    }, []);

    const selectionCtx = useMemo(
      () => ({ selectedRegimeId, onSelectRegime: handleSelectRegime }),
      [selectedRegimeId, handleSelectRegime]
    );

    const tabItems = useMemo(
      () =>
        MATRIX_TABS.map((tab) => ({
          ...tab,
          icon: getTabIcon(tab.value, activeTab, visitedTabs),
          ariaLabel: `${tab.label}, ${getTabStateLabel(
            tab.value,
            activeTab,
            visitedTabs
          )}`,
        })),
      [activeTab, visitedTabs]
    );

    const renderTabContent = (): React.JSX.Element | null => {
      if (!selectedRegime) return null;

      switch (activeTab) {
        case 'populations':
          return (
            <PopulationsTabContent
              populationType={populationType}
              onPopulationTypeChange={handlePopulationTypeChange}
              baselineSelected={baselineSelected}
              toggleBaseline={toggleBaseline}
              criteriaBlocks={criteriaBlocks}
              criteriaErrors={criteriaErrors}
              canAddCriteriaBlock={canAddCriteriaBlock}
              getDimensionOptionsForBlock={getDimensionOptionsForBlock}
              getValueOptions={getValueOptions}
              onAddCriteriaBlock={addCriteriaBlock}
              onRemoveCriteriaBlock={removeCriteriaBlock}
              onCriteriaDimensionChange={handleBlockDimensionChange}
              onCriteriaValuesChange={handleBlockValuesChange}
              readOnly={readOnly}
              pellSelected={pell}
            />
          );
        case 'definition':
          return (
            <DefinitionTabContent
              awardType={awardType}
              calculatedBy={calculatedBy}
              ranges={ranges}
              academicRanks={
                academicRanks as Array<{
                  id: string | number;
                  rank: string | number;
                }>
              }
              onAwardTypeChange={onAwardTypeChange}
              onCalculatedByChange={onCalculatedByChange}
              onRangesChange={onRangesChange}
              readOnly={readOnly}
              pell={pell}
            />
          );
        case 'award-calculation':
          return (
            <AwardCalculationTabContent
              ranges={ranges}
              calculatedBy={calculatedBy}
              rankIds={rankIds}
              rankIdToDisplayNumber={rankIdToDisplayNumber}
              getCell={getCell}
              updateCell={updateCell}
              regimeId={
                selectedRegime?.id != null ? Number(selectedRegime.id) : null
              }
              readOnly={readOnly}
            />
          );
        default:
          return null;
      }
    };

    const renderValidationStatus = (): React.JSX.Element | null => {
      if (isValidationLoading) {
        return (
          <div className="flex items-center gap-2 pr-3">
            <span className="size-1 shrink-0 rounded-full bg-amber-500" />
            <span className="text-sm text-gray-500 whitespace-nowrap">
              Recalculating student pool...
            </span>
          </div>
        );
      }

      if (!validationData) return null;

      const studentsOutOfRegime = validationData.studentsOutOfRegime ?? 0;
      const studentsOutOfCell = validationData.studentsOutOfCell ?? 0;

      if (!studentsOutOfRegime && !studentsOutOfCell) {
        return (
          <div className="flex items-center pr-3">
            <span className="text-xs font-semibold text-success-600 whitespace-nowrap">
              {`You're all set – all students are included`}
            </span>
          </div>
        );
      }

      return (
        <div className="flex flex-col items-end gap-0.5 pr-3">
          {renderCountBadge('Not included in a regime', studentsOutOfRegime)}
          {renderCountBadge(
            'Missing data for award calculation',
            studentsOutOfCell
          )}
        </div>
      );
    };

    return (
      <div
        className="relative w-full max-w-full overflow-hidden h-full"
        data-testid="matrix-step"
      >
        <Loader loading={isLoading} fullScreen={false} />

        {!isLoading && !hasRegimes && (
          <EmptySpaceContent
            customClass="h-[55vh]"
            text="No regimes have been added yet"
            linkShow={!readOnly}
            linkText="Add regime"
            onClick={handleAddRegime}
          />
        )}

        {!isLoading && hasRegimes && (
          <div className="flex w-full max-w-full min-w-0 h-[calc(100vh-19rem)] border border-navy-200 rounded-md overflow-hidden">
            <div
              className={classNames(
                'flex shrink-0 overflow-hidden transition-[width,opacity,visibility] duration-300 ease-in-out',
                isSidebarCollapsed
                  ? 'w-0 opacity-0 invisible'
                  : 'w-80 opacity-100 visible'
              )}
            >
              <RegimeSelectionProvider value={selectionCtx}>
                <RegimesSidebar
                  userId={userId ?? ''}
                  modelId={modelId ?? ''}
                  groups={regimeGroups}
                  regimes={regimesList}
                  onAddRegime={handleAddRegime}
                  hideToggle={isToggleInTabBar}
                  onToggleCollapse={handleToggleSidebar}
                  focusToggleOnMount={hasToggledSidebarRef.current}
                  onGuardAction={guardAction}
                  readOnly={readOnly}
                />
              </RegimeSelectionProvider>
            </div>

            <div className="w-0 flex-1 flex flex-col min-w-0 overflow-hidden">
              <div className="flex items-center justify-between border-b pt-3">
                <div className="flex items-center min-w-0">
                  <div
                    className={classNames(
                      'shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out',
                      isSidebarCollapsed ? 'w-16' : 'w-0'
                    )}
                  >
                    {isToggleInTabBar && (
                      <div className="px-3">
                        <RegimesSidebarToggle
                          isCollapsed
                          onToggle={handleToggleSidebar}
                          focusOnMount={hasToggledSidebarRef.current}
                        />
                      </div>
                    )}
                  </div>
                  <Tabs
                    items={tabItems}
                    activeTab={activeTab}
                    onSelect={handleTabSelect}
                    customClass="border-none pl-2"
                    data-testid="matrix-tabs"
                  />
                </div>
                {renderValidationStatus()}
              </div>
              <div className="flex-1 min-h-0 overflow-auto scrollbar-thin">
                {renderTabContent()}
              </div>
            </div>
          </div>
        )}

        {isModalOpen && (
          <AddRegimeModal
            userId={userId ?? ''}
            modelId={modelId ?? ''}
            existingGroups={regimeGroups}
            onClose={() => setIsModalOpen(false)}
            onSuccess={() => setIsModalOpen(false)}
          />
        )}

        <UnsavedChangesModal
          open={pendingAction !== null}
          loading={isSavingBeforeAction}
          onSaveAndContinue={handleSaveAndContinue}
          onDiscardAndContinue={handleDiscardAndContinue}
          onClose={handleCloseUnsaved}
        />
      </div>
    );
  }
);

MatrixStep.displayName = 'MatrixStep';

export default MatrixStep;
